/**
 * The traffic Reach subsystem (M4 spec §10.2-§10.4, plan I4): strategic agents in the player's system fly as
 * contacts and real ships. Discovered from `src/<dir>/subsystem.ts` (M3); order 450.
 *
 * - It reads the strategic client through `strategicBoot()?.client` (I2's service; D-TBD: traffic subsystem
 *   client access) and subscribes once to `onManifest` and `onDelta`. Messages are queued and applied in `step`,
 *   where the sim is at hand (a manifest replaces the store and despawns the real ships).
 * - Local Reach time advances by `SIM_DT × warp` each tick and snaps to `client.clock.time` when it moves.
 * - Per tick: contacts, the tier step (promote/demote), step reports and the `syncS` batch. The traffic pilot runs
 *   on the sim's `control` stage and the kill reader on its `deaths` stage (`registerSimStage`, once per sim).
 * - M6 (plan P22): own corporation ships are promoted first and dressed by `fleet-link/presence.ts` (`onPromoted`);
 *   the client's fleet alerts are routed into the presence inbox the Reach HUD toasts from.
 * - Held frames (docked: no sim ticks, while the Reach clock runs on) call `held` → `stepTrafficHeld`: contacts follow
 *   the clock and ended steps are reported, so the local system's agents do not wait in their docks for the undock.
 * - `onSystemLeave` despawns every real ship and clears the store (M3's `resetLocalSpace` also removes ships).
 * - M9 CH-3b: the manifest's disabled wrecks surface as hulks (`spawnManifestWrecks`); a hulk goes when its record
 *   leaves a later manifest, when its disabled record expires on the Reach clock (`expireManifestHulks`, per tick and
 *   held frame) or when the system is left.
 *
 * Quiet ticks allocate nothing; message application, promotions, reports and sync batches are message rate.
 */
import hullsJson from '../data/strategic/hulls.json'
import localJson from '../data/strategic/local.json'
import { beginTrafficPilot, createTrafficPilotStage, type TrafficBay, type TrafficBays, type TrafficPilotEnv } from '../ai/traffic'
import type { ShipStream } from '../assets/shipStream'
import type { AutodockConfig } from '../docking/autopilot'
import { moorConfig } from '../docking/moorConfig'
import type { SalvageRuntime } from '../space/contracts'
import { EV, eventSlot, firstReadable } from '../combat/events'
import { SIM_DT } from '../core/loop'
import { logger } from '../core/logger'
import { registerContactSource } from '../hud/hudModel'
import type { ReachCore, ReachSubsystem } from '../reach/runtime/types'
import { registerSimStage, type Sim } from '../sim'
import type { StrategicClient } from '../strategic/client'
import { bindFleetAlerts, presenceHook } from '../fleet-link/presence'
import { kitFor, type Kit } from '../ships/kits'
import { localConfigSchema, type LocalConfig } from '../strategic/schemas'
import type { LocalAgent, LocalDelta, LocalManifest, LocalStep, LocalSync, StrategicCommand, WreckMakerId } from '../strategic/types'
import { strategicBoot } from '../strategic-link/service'
import { Agent } from './components'
import { STANDBY } from '../data/standbyConfig'
import { advanceContacts, applyJob, applyStep, clearContacts, createContactStore, isDockedLike, isStandbyCandidate, refreshAllStandby, removeAgent, setStationNames, upsertAgent, type BayEndResolver, type ContactStore } from './contacts'
import { clearManifestHulks, createManifestHulks, expireManifestHulks, spawnManifestWrecks, type ManifestHulks, type WreckKeepOut } from './encounterPort'
import { createHudContactSource } from './hudContacts'
import { createTierState, demote, despawnAll, stepTiers, type TierEnv, type TierState } from './realShips'
import { createNodeTable, fillNodeTable, markArrived, nodeResolver, reportSteps, type NodeTable } from './steps'
import { standbyPoint } from './standbyPoint'
import { createKillReader, createSyncState, stepSync, type SyncState } from './sync'

/** The client calls traffic makes. */
export type TrafficClient = Pick<StrategicClient, 'onManifest' | 'onDelta' | 'syncLocal' | 'command' | 'clock'>

/**
 * What the traffic layer needs from M2's space runtime, bound by whoever holds it (boot or the M2 bridge; the
 * subsystem cannot reach the services): the docking bays and tuning for AI docking (S-M2-5) and the salvage
 * runtime for the manifest's off-screen wrecks (S-M2-9). Unbound → ships dock on reaching the station, no wrecks.
 * M8 MC-f5b: the traffic runtime also binds the salvage runtime's `makerOf` hook, so a local kill's wreck names who
 * built the ship.
 * M10 P1b1: `ships` is the ship model stream. After a manifest applies, the models of its agents and wreck hulls are
 * requested, and so are those of each delta spawn (message rate). Boot binds it outside the M2 guard (pure `reach`
 * mode has traffic but no M2). Unbound → nothing is requested (every ship is then a boot model).
 */
export interface TrafficPorts {
  salvage: Pick<SalvageRuntime, 'spawnCombatWreck' | 'bindMakerOf' | 'spawnHulk' | 'despawnHulk'> | null
  bays: TrafficBays | null
  docking: AutodockConfig | null
  ships: Pick<ShipStream, 'request' | 'assetsOfDefs'> | null
  /** SC-5d1: station legs leave from the bay mouth and end at the bay entry (`bays.ts createStationBayEnds`); null → centres. */
  bayEnds: BayEndResolver | null
  /** SC-5e1: off-screen wrecks and hulks spawn outside station hulls (`bays.ts createStationKeepOut`); null → as recorded. */
  keepOut: WreckKeepOut | null
  /**
   * M10 P4b4a: dresses a just-promoted ship in its operator kit or none (boot binds the ship views' `setKit`).
   * Unbound → no kit parts are drawn.
   */
  kits: { setKit(eid: number, kit: Kit | null): void } | null
}

const ports: TrafficPorts = { salvage: null, bays: null, docking: null, ships: null, bayEnds: null, keepOut: null, kits: null }

/** Binds (or, with nulls, unbinds) the ports every traffic runtime reads. */
export function bindTrafficPorts(p: Partial<TrafficPorts>): void {
  if (p.salvage !== undefined) ports.salvage = p.salvage
  if (p.bays !== undefined) ports.bays = p.bays
  if (p.docking !== undefined) ports.docking = p.docking
  if (p.ships !== undefined) ports.ships = p.ships
  if (p.bayEnds !== undefined) ports.bayEnds = p.bayEnds
  if (p.keepOut !== undefined) ports.keepOut = p.keepOut
  if (p.kits !== undefined) ports.kits = p.kits
}

/**
 * M10 P4b4a: the operator kit of contact `slot`'s agent, keyed on its stable serial id (`ag-<seq>`, never the slot;
 * review m15): the Mutual's and the Wreckers' AI ships only (an own ship carries the player's fit). Message rate.
 */
export function kitOfSlot(s: ContactStore, slot: number): Kit | null {
  if (s.own[slot] === 1 || s.fleet[slot] !== null) return null
  return kitFor(s.faction[slot], s.shipDef[slot], s.ids[slot])
}

/** The kit of a manifest or delta agent (an own ship has none). Message rate. */
function kitOfAgent(a: LocalAgent): Kit | null {
  return a.fleet !== undefined ? null : kitFor(a.faction, a.shipDef, a.id)
}

/** The tier env's `onPromoted`: the M6 presence look, then the kit dressing (bound once; allocation-free per call). */
function promotedHook(store: ContactStore): (eid: number, slot: number) => void {
  const presence = presenceHook(store)
  return (eid, slot) => {
    presence(eid, slot)
    ports.kits?.setKit(eid, kitOfSlot(store, slot))
  }
}

/** hulls.json id → the local ship def its wrecks use. */
const HULL_SHIP_DEF = new Map<string, string>(hullsJson.hulls.map((h) => [h.id, h.shipDef]))

/** Local agents a system can hold (a busy system has a few hundred). */
export const TRAFFIC_CAPACITY = 1024

/**
 * The layout fields `stepTraffic` and `stepTrafficHeld` take when there are none (no layout yet). One shared list: an
 * `[]` written at a per-tick call site is a fresh 32 B array every tick (D-TBD).
 */
export const NO_TRAFFIC_FIELDS: readonly { id: string; center: readonly [number, number, number] }[] = []

export interface TrafficRuntime {
  readonly store: ContactStore
  readonly nodes: NodeTable
  readonly tier: TierState
  readonly sync: SyncState
  readonly cfg: LocalConfig
  readonly pilot: TrafficPilotEnv
  readonly tierEnv: TierEnv
  /** Ship def and AI profile indices by id, filled from the sim at staging. */
  readonly defs: Map<string, number>
  readonly profiles: Map<string, number>
  lastClock: number
  pendingManifest: LocalManifest | null
  readonly pendingDeltas: LocalDelta[]
  client: TrafficClient | null
  /** The sim whose stages carry the pilot and the kill reader. */
  stagedSim: Sim | null
  readonly unstage: (() => void)[]
  /** Off-screen wreck ids already spawned as M2 wrecks (cleared on leaving the system). */
  readonly wrecksSpawned: Set<string>
  /** M9 CH-3b: the hulks the manifest's disabled records spawned (despawned when their record leaves or expires). */
  readonly manifestHulks: ManifestHulks
  /**
   * M8 MC-f5b: the victims of the last kill-reader pass (ships the reader removed from the store) and their makers,
   * so the salvage hook still names them whichever `deaths` stage runs first. Valid for sim tick `killTick[0]`.
   */
  readonly killEid: Int32Array
  readonly killMaker: (WreckMakerId | null)[]
  readonly killTick: Float64Array
  /** BL-1a: [0] the store count at the last stand-by ranking (a kill that removes a contact triggers a re-rank). */
  readonly standbyCount: Int32Array
  /** BL-1b: each slot's rank among its station's shown stand-by ships (scratch for `placeStandby`; -1 unranked). */
  readonly standbyRank: Int32Array
  killCount: number
  /** The salvage runtime `makerOf` is bound to (rebound when the ports change). */
  boundSalvage: TrafficPorts['salvage']
  /** Callbacks bound once (a per-tick closure would allocate). */
  readonly makerOf: (eid: number) => WreckMakerId | null
  readonly sendCmd: (cmd: StrategicCommand) => void
  readonly removeSlot: (slot: number) => void
  readonly sendSync: (batch: readonly LocalSync[]) => void
}

function logSendError(err: unknown): void {
  logger.warn('traffic: a report to the strategic core failed', err)
}

/** Creates the traffic runtime (one per subsystem; tests make their own). */
export function createTrafficRuntime(cfg: LocalConfig = localConfigSchema.parse(localJson)): TrafficRuntime {
  const store = createContactStore(TRAFFIC_CAPACITY)
  const defs = new Map<string, number>()
  const profiles = new Map<string, number>()
  const pilot: TrafficPilotEnv = {
    store,
    cfg,
    docking: null,
    bays: null,
    profileIndex: (id) => profiles.get(id) ?? -1,
    arrived: (slot) => markArrived(store, slot),
  }
  const rt: TrafficRuntime = {
    store,
    nodes: createNodeTable(),
    tier: createTierState(TRAFFIC_CAPACITY),
    sync: createSyncState(),
    cfg,
    pilot,
    tierEnv: { store, cfg, pilot, defIndexOf: (id) => defs.get(id) ?? -1, kitOf: (slot) => kitOfSlot(store, slot), onPromoted: promotedHook(store) },
    defs,
    profiles,
    lastClock: -1,
    pendingManifest: null,
    pendingDeltas: [],
    client: null,
    stagedSim: null,
    unstage: [],
    wrecksSpawned: new Set(),
    manifestHulks: createManifestHulks(),
    killEid: new Int32Array(TRAFFIC_CAPACITY).fill(-1),
    killMaker: new Array<WreckMakerId | null>(TRAFFIC_CAPACITY).fill(null),
    killTick: new Float64Array(1).fill(-1),
    standbyCount: new Int32Array(1),
    standbyRank: new Int32Array(TRAFFIC_CAPACITY).fill(-1),
    killCount: 0,
    boundSalvage: null,
    makerOf: (eid) => killedShipMaker(rt, eid),
    sendCmd: (cmd) => send(rt, cmd),
    removeSlot: (slot) => {
      const sim = rt.stagedSim
      if (sim !== null && store.eid[slot] >= 0) demote(sim, rt.tierEnv, rt.tier, slot)
      removeAgent(store, slot)
      if (sim !== null) refreshStandbyOf(rt, sim)
    },
    sendSync: (batch) => {
      rt.client?.syncLocal(batch).catch(logSendError)
    },
  }
  return rt
}

/** Sends a command to the core (message rate); failures are logged. */
function send(rt: TrafficRuntime, cmd: StrategicCommand): void {
  if (rt.client !== null) rt.client.command(cmd).catch(logSendError)
}

/**
 * M8 MC-f5b: who built the ship in contact slot `slot`: its faction when that faction makes parts, else null (the
 * raiders and the guardian cell, D-430; the player corporation's own ships).
 */
export function makerOfSlot(s: ContactStore, slot: number): WreckMakerId | null {
  if (s.own[slot] === 1 || s.fleet[slot] !== null) return null
  const f = s.faction[slot]
  return f === 'raider' || f === 'guardian' ? null : f
}

/** The store slot of real ship `eid`, or -1. */
function slotOfEid(s: ContactStore, eid: number): number {
  if (eid < 0) return -1
  const slot = Agent.index[eid]
  return slot >= 0 && slot < s.count && s.eid[slot] === eid ? slot : -1
}

/**
 * The salvage `makerOf` hook: the maker of killed ship `eid` if it is (or this tick was) a traffic ship, else null.
 * Allocation-free.
 */
function killedShipMaker(rt: TrafficRuntime, eid: number): WreckMakerId | null {
  const slot = slotOfEid(rt.store, eid)
  if (slot >= 0) return makerOfSlot(rt.store, slot)
  const sim = rt.stagedSim
  if (sim === null || rt.killTick[0] !== sim.tick) return null
  for (let k = 0; k < rt.killCount; k++) if (rt.killEid[k] === eid) return rt.killMaker[k]
  return null
}

/** Notes the makers of this pass's kill victims (`from` = the kill reader's cursor) before the reader removes them. */
function captureKillMakers(rt: TrafficRuntime, sim: Sim, from: number): void {
  const ring = sim.events
  rt.killTick[0] = sim.tick
  rt.killCount = 0
  for (let i = firstReadable(ring, from); i < ring.head; i++) {
    const e = eventSlot(i)
    if (ring.type[e] !== EV.kill) continue
    const slot = slotOfEid(rt.store, ring.eid[e])
    if (slot < 0 || rt.killCount >= rt.killEid.length) continue
    rt.killEid[rt.killCount] = ring.eid[e]
    rt.killMaker[rt.killCount++] = makerOfSlot(rt.store, slot)
  }
}

/** Binds the salvage runtime's `makerOf` hook to this runtime when the salvage port changes (unbinding the old one). */
function bindSalvageMaker(rt: TrafficRuntime): void {
  const salvage = ports.salvage
  if (salvage === rt.boundSalvage) return
  rt.boundSalvage?.bindMakerOf(null)
  salvage?.bindMakerOf(rt.makerOf)
  rt.boundSalvage = salvage
}

/**
 * Registers the pilot and kill reader on `sim` (once per sim) and indexes its defs and AI profiles. Also keeps the
 * salvage `makerOf` hook bound (MC-f5b).
 */
export function stageTraffic(rt: TrafficRuntime, sim: Sim): void {
  bindSalvageMaker(rt)
  if (rt.stagedSim !== sim) restageTraffic(rt, sim)
}

/**
 * `stageTraffic`'s once-per-sim part. Its own function because its `deaths` closure captures `rt` and the kill
 * reader: a function that creates a closure builds the captured variables' context on every call, early return or
 * not, and V8 drops that only while the restaging path is cold. Inside `stageTraffic` it cost 48 B a tick once a
 * runtime had restaged a few times (D-TBD).
 */
function restageTraffic(rt: TrafficRuntime, sim: Sim): void {
  for (let i = 0; i < rt.unstage.length; i++) rt.unstage[i]()
  rt.unstage.length = 0
  rt.stagedSim = sim
  rt.defs.clear()
  // First wins: a kit def slot (M10 P4b4c: appended at boot, after every base) keeps its base's id and never takes it.
  for (let i = 0; i < sim.defs.length; i++) if (!rt.defs.has(sim.defs[i].id)) rt.defs.set(sim.defs[i].id, i)
  rt.profiles.clear()
  const list = sim.data.aiProfiles.profiles
  for (let i = 0; i < list.length; i++) rt.profiles.set(list[i].id, i)
  rt.unstage.push(registerSimStage(sim, 'control', createTrafficPilotStage(rt.pilot, SIM_DT)))
  const killReader = createKillReader(rt.store, rt.tier, rt.sendCmd)
  killReader.cursor = sim.events.head
  rt.killCount = 0
  // MC-f5b: the victims' makers are noted before the reader removes them from the store (one closure per staging).
  const deaths = (s: Sim): void => {
    captureKillMakers(rt, s, killReader.cursor)
    killReader(s)
  }
  rt.unstage.push(registerSimStage(sim, 'deaths', deaths))
}

/** Binds the client (once): manifests and deltas are queued for `stepTraffic`. */
export function bindTrafficClient(rt: TrafficRuntime, client: TrafficClient): void {
  rt.client = client
  client.onManifest((m) => {
    rt.pendingManifest = m
    rt.pendingDeltas.length = 0
  })
  client.onDelta((d) => {
    rt.pendingDeltas.push(d)
  })
}

/** Applies the queued manifest and deltas (message rate). `fields` are the layout's fields for the node table. */
export function applyPending(rt: TrafficRuntime, sim: Sim, fields: readonly { id: string; center: readonly [number, number, number] }[]): void {
  const s = rt.store
  const resolve = nodeResolver(rt.nodes)
  const m = rt.pendingManifest
  if (m !== null) {
    rt.pendingManifest = null
    despawnAll(sim, rt.tierEnv, rt.tier)
    clearContacts(s)
    setStationNames(s, m.stations)
    s.time[0] = m.time
    fillNodeTable(rt.nodes, m.stations, fields)
    if (ports.salvage !== null) {
      const defs = rt.defs
      spawnManifestWrecks(ports.salvage, m.wrecks, (hull) => defs.get(HULL_SHIP_DEF.get(hull) ?? '') ?? -1, rt.wrecksSpawned, rt.manifestHulks, m.time, ports.keepOut)
    }
    for (let i = 0; i < m.agents.length; i++) {
      if (upsertAgent(s, m.agents[i], rt.store.time[0], resolve, ports.bayEnds, rt.cfg.holdOrbitM) < 0) logger.warn(`traffic: store full, agent ${m.agents[i].id} skipped`)
    }
    if (ports.ships !== null) requestManifestShips(ports.ships, sim, s, m)
  }
  for (let k = 0; k < rt.pendingDeltas.length; k++) {
    const d = rt.pendingDeltas[k]
    if (ports.ships !== null && d.spawns.length > 0) requestSpawnShips(ports.ships, sim, d)
    for (let i = 0; i < d.spawns.length; i++) {
      const slot = upsertAgent(s, d.spawns[i], rt.store.time[0], resolve, ports.bayEnds, rt.cfg.holdOrbitM)
      if (slot < 0) logger.warn(`traffic: store full, agent ${d.spawns[i].id} skipped`)
      // A refresh of a known ship (its fleet block changed, D-459): a real ship takes the step like a step delta.
      else if (s.eid[slot] >= 0) realShipStep(rt, sim, slot, d.spawns[i].step)
    }
    for (let i = 0; i < d.steps.length; i++) {
      const slot = s.slotById.get(d.steps[i].id)
      if (slot === undefined) continue
      applyJob(s, slot, d.steps[i].job)
      applyStepToSlot(rt, sim, slot, d.steps[i].step)
    }
  }
  rt.pendingDeltas.length = 0
  refreshStandbyOf(rt, sim)
}

/**
 * RR-F1 BL-1a: re-ranks the stand-by contacts per station (`standby.json standbyVisiblePerStation`, all roles under one
 * cap) after applied steps, spawns or removals, and demotes a real ship its station's cap now hides. Off (0): nothing
 * runs, so every contact's `standby` stays 0 and visibility, promotion and demotion are today's. Message rate.
 */
function refreshStandbyOf(rt: TrafficRuntime, sim: Sim): void {
  const n = STANDBY.standbyVisiblePerStation
  const s = rt.store
  rt.standbyCount[0] = s.count
  if (n <= 0) return
  refreshAllStandby(s, n)
  placeStandby(rt)
  for (let i = 0; i < s.count; i++) if (s.eid[i] >= 0 && s.standby[i] === 2) demote(sim, rt.tierEnv, rt.tier, i)
}

// BL-1b scratch (D-014).
const standbyBay: TrafficBay = { frame: null, pos: { x: 0, y: 0, z: 0 }, radius: 0 }
const standbyAt = { x: 0, y: 0, z: 0 }

/** BL-1b: writes the bay of the station (or base) node `id` to `standbyBay`; false with no bay frame yet. */
function standbyBayOf(rt: TrafficRuntime, bays: TrafficBays, id: string): boolean {
  if (bays.bayOf(id, standbyBay) && standbyBay.frame !== null) return true
  // As `moorSpawnPoint` does: the station whose bounds hold the node's position (its centre).
  const p = rt.nodes.pos.get(id)
  if (bays.bayAt === undefined || p === undefined) return false
  return bays.bayAt(p[0], p[1], p[2], standbyBay) && standbyBay.frame !== null
}

/**
 * RR-F1 BL-1b: puts each shown stand-by ship (`standby` 1, just ranked) at its station's stand-by point
 * (`standbyPoint`: the moor ring's slot `rank mod slots`, `standbyClearM` outside the larger of the station-radius-aware
 * ring and the hold orbit `R + holdOrbitM`, BL-R2).
 * Its rank is its place among its station's shown ships by slot order, so the points are distinct and deterministic.
 * With no bay frame (no bays port, or the station's model not attached yet) the ship is hidden (2): never drawn at
 * the station centre. A contact moves to the point; a real ship whose point moved holds there afresh (its pilot
 * restarts). Message rate; allocation-free.
 */
function placeStandby(rt: TrafficRuntime): void {
  const s = rt.store
  const rank = rt.standbyRank
  const bays = ports.bays
  const moor = rt.pilot.moor ?? moorConfig()
  const clearM = STANDBY.standbyClearM
  const holdOrbitM = rt.cfg.holdOrbitM
  for (let i = 0; i < s.count; i++) rank[i] = -1
  for (let i = 0; i < s.count; i++) {
    if (s.standby[i] !== 1 || rank[i] >= 0) continue
    // Rank this station's shown ships (slot order), then place them round one bay lookup.
    const id = s.targetId[i]
    let k = 0
    for (let j = i; j < s.count; j++) if (s.standby[j] === 1 && s.targetId[j] === id) rank[j] = k++
    const frame = bays !== null && standbyBayOf(rt, bays, id) ? standbyBay.frame : null
    for (let j = i; j < s.count; j++) {
      if (s.standby[j] !== 1 || s.targetId[j] !== id) continue
      if (frame === null) {
        s.standby[j] = 2
        continue
      }
      standbyPoint(standbyBay.pos, standbyBay.radius, frame.out, rank[j], moor, holdOrbitM, clearM, standbyAt)
      if (s.tx[j] === standbyAt.x && s.ty[j] === standbyAt.y && s.tz[j] === standbyAt.z) continue
      s.tx[j] = s.fx[j] = standbyAt.x
      s.ty[j] = s.fy[j] = standbyAt.y
      s.tz[j] = s.fz[j] = standbyAt.z
      if (s.eid[j] < 0) {
        s.x[j] = standbyAt.x
        s.y[j] = standbyAt.y
        s.z[j] = standbyAt.z
      } else {
        beginTrafficPilot(rt.pilot, s.eid[j], j)
      }
    }
  }
}

/**
 * M10 P1b1: requests the models of the store's ships (just filled from `m`) and of `m`'s wreck hulls. M10 P4b4a: and
 * the operator part GLB of each kit operator with a kitted agent present (at most two per system). Message rate.
 */
function requestManifestShips(ships: NonNullable<TrafficPorts['ships']>, sim: Sim, s: ContactStore, m: LocalManifest): void {
  const defIds = new Set<string>()
  const parts = new Set<string>()
  for (let i = 0; i < s.count; i++) {
    defIds.add(s.shipDef[i])
    const kit = kitOfSlot(s, i)
    if (kit !== null) parts.add(kit.partAsset)
  }
  for (let i = 0; i < m.wrecks.length; i++) {
    const def = HULL_SHIP_DEF.get(m.wrecks[i].hull)
    if (def !== undefined) defIds.add(def)
  }
  if (defIds.size > 0 || parts.size > 0) ships.request(withParts(ships.assetsOfDefs(defIds, sim.defs), parts))
}

/** M10 P1b1: requests the models of a delta's spawns (agents new to the system, or refreshed), with their kit parts (P4b4a). Message rate. */
function requestSpawnShips(ships: NonNullable<TrafficPorts['ships']>, sim: Sim, d: LocalDelta): void {
  const defIds = new Set<string>()
  const parts = new Set<string>()
  for (let i = 0; i < d.spawns.length; i++) {
    defIds.add(d.spawns[i].shipDef)
    const kit = kitOfAgent(d.spawns[i])
    if (kit !== null) parts.add(kit.partAsset)
  }
  ships.request(withParts(ships.assetsOfDefs(defIds, sim.defs), parts))
}

/** `ids` with the part GLBs not already in it appended. Message rate. */
function withParts(ids: string[], parts: ReadonlySet<string>): string[] {
  for (const p of parts) if (!ids.includes(p)) ids.push(p)
  return ids
}

function applyStepToSlot(rt: TrafficRuntime, sim: Sim, slot: number, step: LocalStep): void {
  applyStep(rt.store, slot, step, rt.store.time[0], nodeResolver(rt.nodes), ports.bayEnds, rt.cfg.holdOrbitM)
  if (rt.store.eid[slot] >= 0) realShipStep(rt, sim, slot, step)
}

/**
 * A real ship's new step (already applied): docked, the core says it is inside its station and it goes; a stand-by
 * candidate with the key on waits for the batch's `refreshStandbyOf` (BL-R2); else its pilot restarts.
 */
function realShipStep(rt: TrafficRuntime, sim: Sim, slot: number, step: LocalStep): void {
  // Docked-like (BL-1a): docked, or a stand-by its station's cap hides (re-ranked after the batch, refreshStandbyOf).
  if (step.kind === 'docked' || isDockedLike(rt.store, slot)) demote(sim, rt.tierEnv, rt.tier, slot)
  // BL-R2: `applyStep` aimed it at the station centre (its hold orbit); `placeStandby` restarts it at its stand-by
  // point, or `refreshStandbyOf` hides and demotes it, so starting its pilot here would only be overwritten.
  else if (STANDBY.standbyVisiblePerStation > 0 && isStandbyCandidate(rt.store, slot)) return
  else beginTrafficPilot(rt.pilot, rt.store.eid[slot], slot)
}

/**
 * M9 CH-3b: despawns the manifest hulks whose disabled records expired on the Reach clock (after `stepTraffic` or
 * `stepTrafficHeld`; kept out of them so their hot bodies stay as they were). Allocation-free.
 */
export function expireTrafficHulks(rt: TrafficRuntime): void {
  const salvage = ports.salvage
  if (salvage !== null && rt.store.time[0] >= rt.manifestHulks.nextExpiry) expireManifestHulks(salvage, rt.manifestHulks, rt.store.time[0])
}

/** One traffic step (per sim tick). Allocation-free on a quiet tick. */
export function stepTraffic(rt: TrafficRuntime, sim: Sim, fields: readonly { id: string; center: readonly [number, number, number] }[]): void {
  const client = rt.client
  if (client === null) return
  stageTraffic(rt, sim)
  rt.pilot.bays = ports.bays
  rt.pilot.docking = ports.docking
  if (rt.pendingManifest !== null || rt.pendingDeltas.length > 0) applyPending(rt, sim, fields)
  const clock = client.clock
  if (clock !== null && clock.time !== rt.lastClock) {
    rt.lastClock = clock.time
    rt.store.time[0] = clock.time
  } else {
    rt.store.time[0] += SIM_DT * (clock !== null ? clock.warp : 1)
  }
  const s = rt.store
  if (s.count === 0) return
  advanceContacts(s)
  stepTiers(sim, rt.tierEnv, rt.tier)
  reportSteps(s, rt.sendCmd, rt.removeSlot)
  stepSync(rt.sync, s, SIM_DT, rt.cfg.syncS, rt.sendSync)
  // A kill (sync.ts) removed a contact: its station's stand-by ranking moves up (BL-1a; rare, a count compare per tick).
  if (s.count !== rt.standbyCount[0]) refreshStandbyOf(rt, sim)
}

/**
 * One held frame (the player is docked: M2's hold runs no sim ticks, while the clock gate keeps the Reach clock
 * running). Contacts follow the core's clock and ended steps are reported, so the local system's frozen agents
 * (a fleet ship docked here, the AI traffic) leave their docks and finish their legs instead of waiting for the
 * undock. Real ships, tiers and sync wait for the ticks. Allocation-free on a quiet frame.
 */
export function stepTrafficHeld(rt: TrafficRuntime, sim: Sim, fields: readonly { id: string; center: readonly [number, number, number] }[]): void {
  const client = rt.client
  if (client === null) return
  stageTraffic(rt, sim)
  if (rt.pendingManifest !== null || rt.pendingDeltas.length > 0) applyPending(rt, sim, fields)
  const clock = client.clock
  if (clock === null || clock.time === rt.lastClock) return
  rt.lastClock = clock.time
  rt.store.time[0] = clock.time
  const s = rt.store
  if (s.count === 0) return
  advanceContacts(s)
  reportSteps(s, rt.sendCmd, rt.removeSlot)
}

/** Despawns every real ship and clears the store (the system was left). */
export function leaveTraffic(rt: TrafficRuntime, sim: Sim): void {
  despawnAll(sim, rt.tierEnv, rt.tier)
  clearContacts(rt.store)
  rt.wrecksSpawned.clear()
  clearManifestHulks(ports.salvage, rt.manifestHulks)
  rt.pendingManifest = null
  rt.pendingDeltas.length = 0
}

/** The live subsystem's store (the encounter wrapper counts local raiders in it), or null before the Reach runs. */
let activeStore: ContactStore | null = null

export function activeTrafficStore(): ContactStore | null {
  return activeStore
}

export default function createTrafficSubsystem(): ReachSubsystem {
  const rt = createTrafficRuntime()
  activeStore = rt.store
  let unregisterHud: (() => void) | null = null
  const fieldsOf = (core: ReachCore): readonly { id: string; center: readonly [number, number, number] }[] => core.session.layout?.fields ?? NO_TRAFFIC_FIELDS
  /** Binds the strategic client once it is booted (a career can start docked, so held frames bind too). */
  const bound = (): boolean => {
    if (rt.client !== null) return true
    const client = strategicBoot()?.client
    if (client === undefined) return false
    bindTrafficClient(rt, client)
    bindFleetAlerts(client)
    return true
  }
  return {
    name: 'traffic',
    order: 450,
    init(): void {
      unregisterHud ??= registerContactSource(createHudContactSource(rt.store))
    },
    step(core, sim): void {
      if (!bound()) return
      stepTraffic(rt, sim, fieldsOf(core))
      expireTrafficHulks(rt)
    },
    held(core, sim): void {
      if (!bound()) return
      stepTrafficHeld(rt, sim, fieldsOf(core))
      expireTrafficHulks(rt)
    },
    onSystemLeave(_core, sim): void {
      leaveTraffic(rt, sim)
    },
  }
}
